import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ethers } from 'ethers';
import { Loader2, X } from 'lucide-react';
import NFTOTEM_ABI from '@/lib/abi/nftotem';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { getOpenSeaUrl } from '@/lib/utils';
import { getCurrentNetwork } from '@/lib/ethereum-config';
import { analytics } from '@/lib/analytics';
import { buildAuthMessage } from '@/lib/auth-message';
import { getCubeImageForSnapshot } from '@/lib/seasonal-images';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

interface WalletConnectButtonProps {
  connectWalletConnect: () => void;
  disconnect: () => void;
  provider?: any;
  address: string | null;
  type: string | null;
  connecting: boolean;
  error: string | null;
  className?: string;
  currentCollections: { id: string; name: string; image: string }[];
  tokenId?: number | null;
  onError?: (error: string) => void; // Callback for passing errors to parent component
  walletAddress?: string; // target wallet on page
}

const CONTRACT_ADDRESS = getCurrentNetwork().contractAddress;

// Reliable data persistence with retry mechanism (same as MetaMaskButton)
async function persistTotemDataWithRetry(
  walletAddress: string,
  tokenId: number,
  currentCollections: { id: string; name: string; image: string }[],
  signMessage: (msg: string) => Promise<string>
): Promise<void> {
  const maxRetries = 3;
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`[WalletConnectButton] Persisting totem data (attempt ${attempt + 1}/${maxRetries})`);
      
      // Generate image first
      let imageUrl = '';
      try {
        const imageRes = await fetch('/api/generate-totem-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            walletAddress, 
            collections: currentCollections.map(c => c.id), 
            tokenId,
            isEdit: false // Indicates this is minting, not editing 
          })
        });
        
        if (imageRes.ok) {
          const imageData = await imageRes.json();
          imageUrl = imageData?.data?.metadata?.image || '';
          console.log(`[WalletConnectButton] Generated image URL: ${imageUrl}`);
        }
      } catch (imageError) {
        console.warn('[WalletConnectButton] Image generation failed, continuing without image:', imageError);
      }
      
      // Save ownership data (with signature)
      const issuedAt = Date.now();
      const authMsg = buildAuthMessage({ action: 'create-ownership', walletAddress, issuedAt, tokenId });
      const signature = await signMessage(authMsg);
      const saveRes = await fetch('/api/create-totem-ownership', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress,
          tokenId,
          collections: currentCollections.map(c => ({
            id: c.id,
            name: c.name,
            cubeImage: getCubeImageForSnapshot(c.id) // Get seasonal image if active, otherwise default
          })),
          blockCount: currentCollections.length,
          imageUrl: imageUrl,
          signature,
          issuedAt
        }),
      });
      
      if (!saveRes.ok) {
        const errorData = await saveRes.text();
        throw new Error(`Save failed: ${saveRes.status} ${saveRes.statusText} - ${errorData}`);
      }
      
      const saveData = await saveRes.json();
      if (!saveData.success) {
        throw new Error(`Save API returned error: ${saveData.error}`);
      }
      
      console.log('[WalletConnectButton] Totem ownership saved successfully');
      
      // Verify the data was actually saved
      try {
        const verifyRes = await fetch(`/api/recover-kv-data?wallet=${walletAddress}`);
        const verifyData = await verifyRes.json();
        
        if (verifyData.success && verifyData.data.exists) {
          console.log('[WalletConnectButton] KV data verification successful');
          return; // Success!
        } else {
          throw new Error('KV data verification failed - data not found after save');
        }
      } catch (verifyError) {
        console.warn('[WalletConnectButton] KV verification failed, but save appeared successful:', verifyError);
        return; // Consider it successful if save API succeeded
      }
      
    } catch (error) {
      lastError = error as Error;
      console.error(`[WalletConnectButton] Attempt ${attempt + 1} failed:`, error);
      
      if (attempt < maxRetries - 1) {
        const delay = 1000 * Math.pow(2, attempt); // Exponential backoff
        console.log(`[WalletConnectButton] Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // If we get here, all retries failed
  console.error('[WalletConnectButton] All persistence attempts failed:', lastError);
  
  // Try to recover the data using the recovery API
  try {
    console.log('[WalletConnectButton] Attempting recovery via API...');
    const recoverRes = await fetch('/api/recover-kv-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        walletAddress,
        tokenId,
        collections: currentCollections.map(c => ({
          id: c.id,
          name: c.name,
          cubeImage: getCubeImageForSnapshot(c.id)
        })),
        blockCount: currentCollections.length,
        forceRecover: true
      })
    });
    
    if (recoverRes.ok) {
      const recoverData = await recoverRes.json();
      if (recoverData.success) {
        console.log('[WalletConnectButton] Data recovered successfully via recovery API');
        return;
      }
    }
  } catch (recoverError) {
    console.error('[WalletConnectButton] Recovery API also failed:', recoverError);
  }
  
  // If everything fails, show a warning but don't block the user
  console.warn('[WalletConnectButton] CRITICAL: Could not persist totem data. User should contact support.');
}

export default function WalletConnectButton({ connectWalletConnect, disconnect, provider, address, type, connecting, error, className, currentCollections, tokenId, onError, walletAddress }: WalletConnectButtonProps) {
  const [hasMinted, setHasMinted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [mintPrice, setMintPrice] = useState<string | null>(null);
  const [isFreeMint, setIsFreeMint] = useState<boolean | null>(null);
  const { toast } = useToast();
  const [isWrongNetwork, setIsWrongNetwork] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [attemptedAutoSwitch, setAttemptedAutoSwitch] = useState<boolean>(false);
  const [autoSwitchFailed, setAutoSwitchFailed] = useState<boolean>(false);
  
  // Consider edit available ONLY if connected wallet matches page wallet
  const isOwner = walletAddress ? Boolean(address && address.toLowerCase() === walletAddress.toLowerCase()) : true;
  const userHasMinted = isOwner && hasMinted;

  // Attempt to switch WC wallet to correct network silently
  const ensureCorrectNetwork = async (): Promise<boolean> => {
    try {
      if (!provider) return false;
      const currentNetwork = getCurrentNetwork();
      const chainId = `0x${currentNetwork.chainId.toString(16)}`;
      
      // Try switch first
      await provider.request?.({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId }],
      });
      return true;
    } catch (err: any) {
      // If chain not added, add it
      if (err?.code === 4902 || /Unrecognized chain ID|not added/i.test(err?.message || '')) {
        try {
          const currentNetwork = getCurrentNetwork();
          const chainId = `0x${currentNetwork.chainId.toString(16)}`;
          
          // Define network parameters based on current network
          let networkParams;
          if (currentNetwork.chainId === 1) {
            // Mainnet
            networkParams = {
              chainId,
              chainName: 'Ethereum Mainnet',
              nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
              rpcUrls: [currentNetwork.rpcUrl || 'https://eth-mainnet.g.alchemy.com/v2/demo'],
              blockExplorerUrls: ['https://etherscan.io']
            };
          } else {
            // Sepolia
            networkParams = {
              chainId,
              chainName: 'Sepolia',
              nativeCurrency: { name: 'Sepolia Ether', symbol: 'SEP', decimals: 18 },
              rpcUrls: [currentNetwork.rpcUrl || 'https://rpc.sepolia.org'],
              blockExplorerUrls: ['https://sepolia.etherscan.io']
            };
          }
          
          await provider.request?.({
            method: 'wallet_addEthereumChain',
            params: [networkParams]
          });
          return true;
        } catch (addErr) {
          console.warn('[WalletConnectButton] wallet_addEthereumChain failed:', addErr);
          return false;
        }
      }
      console.warn('[WalletConnectButton] wallet_switchEthereumChain failed:', err);
      return false;
    }
  };

  // Detect network mismatch and auto-switch once (only for OWNER with active WC session/address)
  useEffect(() => {
    const detectAndAutoSwitch = async () => {
      try {
        if (!provider || !address || type !== 'walletconnect' || !isOwner || attemptedAutoSwitch) return;
        const ethersProvider = new ethers.BrowserProvider(provider);
        const network = await ethersProvider.getNetwork();
        const expected = getCurrentNetwork().chainId;
        const mismatch = network.chainId !== BigInt(expected);
        setIsWrongNetwork(mismatch);
        if (mismatch) {
          setAttemptedAutoSwitch(true);
          const switched = await ensureCorrectNetwork();
          if (!switched) setAutoSwitchFailed(true);
          else setAutoSwitchFailed(false);
        }
      } catch (e: any) {
        // Do not force disconnect on non-owner pages; just stop auto-switch
        setAutoSwitchFailed(true);
      }
    };
    detectAndAutoSwitch();
  }, [provider, address, type, isOwner, attemptedAutoSwitch]);

  // Check mint status when connecting (owner-only) via on-chain check and /api/edit-totem
  useEffect(() => {
    const checkMintStatus = async () => {
      if (address && type === 'walletconnect' && isOwner && !isCheckingStatus) {
        setIsCheckingStatus(true);
        try {
          // First, check on-chain status directly via balanceOf
          let hasMintedOnChain = false;
          try {
            if (provider) {
              const ethersProvider = new ethers.BrowserProvider(provider);
              const contract = new ethers.Contract(CONTRACT_ADDRESS, NFTOTEM_ABI, ethersProvider);
              const balance = await contract.balanceOf(address.toLowerCase());
              hasMintedOnChain = Number(balance) > 0;
            }
          } catch (onChainError) {
            console.warn('[WalletConnectButton] On-chain balance check failed:', onChainError);
          }
          
          // Also try to resolve tokenId via edit-totem API (does on-chain hasMinted inside)
          let hasMintedViaAPI = false;
          try {
            const res = await fetch('/api/edit-totem', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                walletAddress: address, 
                collections: currentCollections.length > 0 ? currentCollections.map(c => c.id) : [] 
              })
            });
            const data = await res.json();
            if (data?.success && typeof data?.data?.tokenId === 'number') {
              hasMintedViaAPI = true;
            }
          } catch (apiError) {
            console.warn('[WalletConnectButton] API check failed:', apiError);
          }
          
          // Use on-chain status as source of truth, API as fallback
          const finalHasMinted = hasMintedOnChain || hasMintedViaAPI;
          setHasMinted(finalHasMinted);
          
          if (finalHasMinted) {
            setMintPrice(null);
            setIsFreeMint(null);
          }
        } catch (e) {
          console.error('[WalletConnectButton] Error checking mint status:', e);
          setHasMinted(false);
        } finally {
          setIsCheckingStatus(false);
        }
      }
    };
    checkMintStatus();
  }, [address, type, isOwner, provider]);

  const handleMintOrEdit = async () => {
    setIsLoading(true);
    try {
      if (!address) throw new Error('WalletConnect not connected');
      
      // Check on-chain mint status as source of truth before deciding between mint and edit
      if (!provider) throw new Error('WalletConnect provider not available');
      const ethersProvider = new ethers.BrowserProvider(provider);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, NFTOTEM_ABI, ethersProvider);
      const targetAddr = (walletAddress || address || '').toLowerCase();
      if (!targetAddr) throw new Error('No wallet address available');
      
      // Check on-chain balance to determine if totem has been minted
      const balance = await contract.balanceOf(targetAddr);
      const hasMintedOnChain = Number(balance) > 0;
      
      // Use on-chain status as source of truth, fallback to local state
      const shouldEdit = hasMintedOnChain || userHasMinted;
      
      // Track attempt event
      if (shouldEdit) {
        await analytics.trackEditAttempt('walletconnect', tokenId || 0, currentCollections.length);
      } else {
        await analytics.trackMintAttempt('walletconnect', '0.003', false, currentCollections.length);
      }
      
      if (shouldEdit) {
        if (!Array.isArray(currentCollections) || !currentCollections.length) {
          throw new Error('No collections for edit');
        }
        let editTokenId = isOwner ? tokenId : null;
        // Always resolve tokenId for the CONNECTED wallet if missing or not owner
        if (!editTokenId) {
          try {
            const res = await fetch('/api/edit-totem', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                walletAddress: address,
                collections: currentCollections.map(c => c.id)
              })
            });
            const data = await res.json();
            if (data?.success && data?.data?.tokenId) {
              editTokenId = data.data.tokenId;
              console.log('[WalletConnectButton] tokenId fetched via /api/edit-totem:', editTokenId);
            } else {
              throw new Error(data?.error || 'Failed to resolve tokenId for edit');
            }
          } catch (e) {
            throw new Error('Failed to resolve tokenId for edit');
          }
        }
        // Get new tokenURI via API
        const res = await fetch('/api/generate-totem-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            walletAddress: address,
            collections: currentCollections.map(c => c.id),
            tokenId: editTokenId,
            isEdit: true // CRITICAL: indicates this is editing
          })
        });
        const data = await res.json();
        // For Edit, we should use metadata URI, not image URI
        // This ensures Rarible and other marketplaces get full metadata with traits
        let newTokenURI = `https://nftotem.ilyakazakov.com/api/metadata/${editTokenId}`;
        
        // Add version if available for cache busting
        if (data?.data?.metadata?.version) {
          newTokenURI += `?v=${data.data.metadata.version}`;
        }
        if (!newTokenURI) throw new Error('Failed to generate new tokenURI');
        // Call edit on-chain using WalletConnect EIP-1193 provider
        // Use signer for transaction (different from read-only contract used earlier)
        const signer = await ethersProvider.getSigner();
        const signerAddr = (await signer.getAddress())?.toLowerCase?.();
        if (signerAddr && address && signerAddr !== address.toLowerCase()) {
          onError?.('Connected WalletConnect account differs from selected address. Please reconnect.');
          return;
        }
        const contractWithSigner = new ethers.Contract(CONTRACT_ADDRESS, NFTOTEM_ABI, signer);
        const tx = await contractWithSigner.edit(newTokenURI);
        await tx.wait();
        
        // Track successful edit
        await analytics.trackEditTotem('walletconnect', editTokenId || 0, currentCollections.length);
        
        // Update KV record with new collections (simple approach)
        try {
          console.log('[WalletConnectButton] Updating KV record with new collections');
          const updateRes = await fetch('/api/admin/update-token-25', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tokenId: editTokenId,
              walletAddress: address,
              collections: currentCollections.map(c => ({
                id: c.id,
                name: c.name,
                cubeImage: getCubeImageForSnapshot(c.id)
              })),
              blockCount: currentCollections.length
            })
          });
          if (updateRes.ok) {
            console.log('[WalletConnectButton] KV record updated successfully');
          } else {
            console.warn('[WalletConnectButton] Failed to update KV record:', updateRes.status);
          }
        } catch (error) {
          console.warn('[WalletConnectButton] Error updating KV record:', error);
        }

        // Success toast with OpenSea link
        try {
          const network = getCurrentNetwork();
          const openseaUrl = getOpenSeaUrl(network.contractAddress, (editTokenId || 0));
          toast({
            title: 'Totem edited successfully!',
            description: 'Your totem has been updated.',
            variant: 'success',
            action: (
              <ToastAction altText="View on OpenSea" onClick={() => window.open(openseaUrl, '_blank')}>
                View on OpenSea
              </ToastAction>
            )
          });
        } catch {
          toast({
            title: 'Totem edited successfully!',
            description: 'Your totem has been updated.',
            variant: 'success',
          });
        }
      } else {
        // Implement Mint via WalletConnect
        if (!provider) throw new Error('WalletConnect provider not available');
        if (!Array.isArray(currentCollections) || !currentCollections.length) {
          throw new Error('No collections for mint');
        }

        const ethersProvider = new ethers.BrowserProvider(provider);
        const signer = await ethersProvider.getSigner();
        const signerAddr = (await signer.getAddress())?.toLowerCase?.();
        const targetAddr = (walletAddress || address || signerAddr || '').toLowerCase();
        if (!targetAddr) throw new Error('No wallet address to mint');
        if (signerAddr && targetAddr && signerAddr !== targetAddr) {
          onError?.('You can’t mint a totem for another wallet. Please switch your WalletConnect account to the target wallet and try again.');
          return;
        }

        const network = await ethersProvider.getNetwork();
        const targetNetwork = getCurrentNetwork();
        const expected = targetNetwork.chainId;
        if (network.chainId !== BigInt(expected)) {
          const switched = await ensureCorrectNetwork();
          if (!switched) {
            onError?.(`Wrong network. Please switch your WalletConnect wallet to ${targetNetwork.name}.`);
            return;
          }
        }

        // Ask backend for tokenURI and price
        const res = await fetch('/api/mint-totem', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ walletAddress: targetAddr, collections: currentCollections.map(c => c.id), prepare: true }),
        });
        const params = await res.json();
        if (!params?.success) throw new Error(params?.error || 'Failed to get mint params');
        const price = params?.data?.price || '0.003';

        // Additional safety check: verify on-chain status before minting
        const contract = new ethers.Contract(CONTRACT_ADDRESS, NFTOTEM_ABI, signer);
        const balance = await contract.balanceOf(targetAddr);
        if (Number(balance) > 0) {
          // User already minted totem, switch to edit flow
          throw new Error('Wallet has already minted a totem. Please use edit instead.');
        }
        
        const tx = await contract.mintTotem(targetAddr, { value: ethers.parseEther(String(price)) });
        const receipt = await tx.wait();

        // Extract tokenId (best-effort)
        let mintedTokenId: number | null = null;
        try {
          const totalSupply = await contract.totalSupply();
          // Contract numbers tokens from 1 → when totalSupply=N the last token = N
          mintedTokenId = Number(totalSupply);
        } catch {}

        // Generate image and persist ownership
        // Use reliable persistence with retry mechanism
        if (mintedTokenId) {
          const signMessage = async (msg: string) => {
            if (!provider) throw new Error('WalletConnect provider not available');
            const ethersProvider = new ethers.BrowserProvider(provider);
            const signer = await ethersProvider.getSigner();
            return await signer.signMessage(msg);
          }
          await persistTotemDataWithRetry(targetAddr, mintedTokenId, currentCollections, signMessage);
          
          // Track successful mint (assuming paid mint for WalletConnect)
          await analytics.trackPaidMint('walletconnect', price, currentCollections.length, mintedTokenId);
        }

        // Generate OpenSea URL
        const currentNetwork = getCurrentNetwork()
        const openseaUrl = mintedTokenId ? getOpenSeaUrl(currentNetwork.contractAddress, mintedTokenId) : null
        
        toast({
          title: 'Totem minted successfully!',
          description: mintedTokenId ? `Your totem has been minted with token ID #${mintedTokenId}.` : 'Your totem has been minted.',
          variant: 'success',
          action: openseaUrl ? (
            <ToastAction altText="View on OpenSea" onClick={() => window.open(openseaUrl, '_blank')}>
              View on OpenSea
            </ToastAction>
          ) : undefined
        });
        setHasMinted(true);
      }
    } catch (e: any) {
      // Friendly error mapping (parity with MetaMaskButton)
      let errorMessage = 'Edit failed. Please try again.';
      const code = e?.code;
      const msg = (e?.message || '') + ' ' + (e?.reason || '');

      if (code === 4001 || code === 5000 || /user denied|user rejected|ACTION_REJECTED/i.test(msg)) {
        errorMessage = 'Transaction was cancelled by user.';
      } else if (code === 'INSUFFICIENT_FUNDS' || /insufficient funds/i.test(msg)) {
        errorMessage = 'Insufficient funds for gas fee. Please ensure you have enough ETH.';
      } else if (code === 'UNPREDICTABLE_GAS_LIMIT' || /gas estimation failed/i.test(msg)) {
        errorMessage = 'Gas estimation failed. Please try again or check your wallet balance.';
      } else if (code === 'CALL_EXCEPTION' || /execution reverted/i.test(msg)) {
        if (/only owner/i.test(msg)) {
          errorMessage = 'This totem belongs to another wallet. You can only mint totems for wallets you own.';
        } else if (/cannot mint totem for another wallet/i.test(msg)) {
          errorMessage = 'You can’t mint a totem for another wallet.';
        } else if (/not the owner of this totem/i.test(msg)) {
          errorMessage = 'This totem belongs to another wallet. You can only mint totems for wallets you own.';
        } else if (/already minted/i.test(msg)) {
          errorMessage = 'This wallet has already minted a totem.';
        } else if (/minting disabled/i.test(msg)) {
          errorMessage = 'Minting is currently disabled.';
        } else if (/no totem found for this wallet/i.test(msg)) {
          errorMessage = 'No totem found for this wallet. Please mint a totem first.';
        } else if (/free mint requires no payment/i.test(msg)) {
          errorMessage = 'This wallet is eligible for free minting. Please use the free mint option.';
        } else if (/insufficient payment/i.test(msg)) {
          errorMessage = 'Insufficient payment. Please send the correct amount of ETH.';
        } else {
          errorMessage = 'Transaction failed. Please check your wallet and try again.';
        }
      } else if (code === 'NETWORK_ERROR' || /network error/i.test(msg)) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      console.warn('[WalletConnectButton] Edit error:', { code, msg, error: e });
      
      // Track error event
      if (userHasMinted) {
        await analytics.trackEditError('walletconnect', errorMessage, tokenId || undefined);
      } else {
        await analytics.trackMintError('walletconnect', errorMessage, undefined, false);
      }
      
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine button text with smooth transitions
  const getButtonText = () => {
    if (isLoading) {
      return userHasMinted ? 'Editing...' : 'Minting...';
    }
    // Don't show paid price until eligibility is received
    if (isFreeMint === null || mintPrice === null) {
      return userHasMinted ? 'Edit Totem' : 'Mint Totem';
    }
    return userHasMinted ? 'Edit Totem' : 'Mint Totem';
  };

  // Determine if loader should be shown
  const showLoader = isLoading || (isWrongNetwork && !autoSwitchFailed);

  // Check if user has any collections
  const hasCollections = currentCollections && currentCollections.length > 0;

  if (address && type === 'walletconnect') {
    // If connected wallet is NOT the owner of the page totem, don't allow editing
    if (!isOwner) {
      return (
        <div className="w-full p-4 bg-gray-100 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-700 mb-2">This totem belongs to another wallet.</p>
          <p className="text-xs text-gray-500">Connect its owner to edit.</p>
          <div className="mt-3 flex gap-2">
            <Button onClick={disconnect} variant="outline" size="sm">Disconnect</Button>
          </div>
        </div>
      );
    }
    // If user has no collections, show informational message instead of button
    if (!hasCollections) {
      return (
        <div className="w-full p-4 bg-gray-100 rounded-lg border border-gray-200 text-center">
          <p className="text-sm text-gray-600 mb-2">
            No NFT collections found in this wallet
          </p>
          <p className="text-xs text-gray-500">
            You need to own at least one NFT from the selected collections to mint a totem.
          </p>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 w-full">
        <Button 
          onClick={handleMintOrEdit} 
          className={`${className} transition-all duration-300 ease-in-out relative overflow-hidden`} 
          disabled={isLoading || isCheckingStatus || isWrongNetwork}
        >
          {showLoader && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          <span className={`transition-opacity duration-200 font-funnel font-medium uppercase ${showLoader ? 'opacity-90' : 'opacity-100'}`}>
            {isWrongNetwork ? (autoSwitchFailed ? `Switch to ${getCurrentNetwork().name}` : 'Switching network...') : getButtonText()}
          </span>
        </Button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={disconnect}
                variant="outline"
                size="icon"
                className={`transition-all duration-300 ease-in-out shrink-0`}
                aria-label="Disconnect"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Disconnect</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Disconnect
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }
  
  return (
    <Button 
      onClick={connectWalletConnect} 
      className={`${className} transition-all duration-300 ease-in-out relative overflow-hidden`} 
      disabled={connecting}
    >
      {connecting && (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      )}
      <span className={`transition-opacity duration-200 font-funnel font-medium uppercase ${connecting ? 'opacity-90' : 'opacity-100'}`}>
        {connecting ? 'Connecting...' : 'WalletConnect'}
      </span>
    </Button>
  );
} 